<script setup lang="ts">
import { computed, ref, watch, type CSSProperties } from 'vue';
import { ContractManagementColumnExtractionScope, type ExtractionPromptSearchEntry } from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaPanel from '@/components/design/composite/GaiaPanel.vue';
import GaiaPickerOption from '@/components/design/composite/GaiaPickerOption.vue';
import GaiaSortControl, {
  type GaiaSortDirection,
  type GaiaSortOption,
} from '@/components/design/composite/GaiaSortControl.vue';
import GaiaContractTypeFilter, {
  contractTypeFilterFromKeys,
} from '@/components/design/domain/contractTypes/GaiaContractTypeFilter.vue';
import { IconClose } from '@/components/design/icons';
import { gaiaShellPadding } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  ExtractionPromptsService,
  type ExtractionPromptFilter,
} from '@/services/contractManagement/extractionPromptsService';
import { responseTypeLabel } from '@/utils/contractManagementView/columnQuestion';

/**
 * Search all of the organisation's extraction prompts and add the picked ones to a contract
 * type. A side panel for Layout's `#drawer` slot, so the type's prompt list stays in view beside
 * it. Prompts already on the type are shown disabled rather than filtered out, so a search for
 * one doesn't look like it doesn't exist (the search can't exclude a contract type anyway).
 */
const props = defineProps<{
  show: boolean;
  contractTypeUid: string;
  contractTypeName: string;
}>();

const emit = defineEmits<{
  'update:show': [value: boolean];
  /** At least one prompt was added: re-fetch the type's prompts. */
  added: [];
}>();

const message = useGaiaToast();

const query = ref('');
/** The contract type filter's picks, as on the prompts page; empty means any. */
const pickedTypeKeys = ref<string[]>([]);
const page = ref(1);
// Twenty rows to a page; the pager offers 10 or 50 instead.
const pageSize = ref(20);
/** The prompt fields the list sorts by; the rows show the display name as the name. */
const SORTS: GaiaSortOption[] = [
  { key: 'display_name', label: 'Name' },
  { key: 'response_type', label: 'Type' },
  { key: 'created_at', label: 'Created at' },
];
const sortKey = ref('display_name');
const sortDirection = ref<GaiaSortDirection>('asc');
const prompts = ref<ExtractionPromptSearchEntry[]>([]);
const total = ref(0);
const loading = ref(false);
const error = ref<string | null>(null);
/** Kept whole, not as uids: adding needs each prompt's current contract types. */
const picked = ref<ExtractionPromptSearchEntry[]>([]);
const adding = ref(false);
let latestRequest = 0;

const filter = computed((): ExtractionPromptFilter | undefined =>
  contractTypeFilterFromKeys(pickedTypeKeys.value)
);

async function load(): Promise<void> {
  const request = ++latestRequest;
  loading.value = true;
  error.value = null;
  try {
    const result = await ExtractionPromptsService.search({
      filter: filter.value,
      text: query.value.trim(),
      offset: (page.value - 1) * pageSize.value,
      limit: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
    });
    if (request !== latestRequest) return;
    prompts.value = result.results;
    total.value = result.result_count;
  } catch (error_) {
    if (request !== latestRequest) return;
    error.value = error_ instanceof Error ? error_.message : 'Failed to load prompts';
    prompts.value = [];
    total.value = 0;
  } finally {
    if (request === latestRequest) loading.value = false;
  }
}

watch(
  () => props.show,
  show => {
    if (!show) return;
    // A changed filter or sort reloads through its own watcher; loading here too would search twice.
    const reloadsItself =
      pickedTypeKeys.value.length > 0 ||
      sortKey.value !== 'display_name' ||
      sortDirection.value !== 'asc';
    query.value = '';
    // Only when set: a new empty array would trigger the filter watcher's reload as well.
    if (pickedTypeKeys.value.length) pickedTypeKeys.value = [];
    sortKey.value = 'display_name';
    sortDirection.value = 'asc';
    picked.value = [];
    if (!reloadsItself) applySearch();
  }
);

watch([page, pageSize], () => void load());

// A new filter or sort, like a new search term, starts again at page 1.
watch([pickedTypeKeys, sortKey, sortDirection], () => {
  applySearch();
});

function applySearch(): void {
  if (page.value === 1) void load();
  else page.value = 1;
}

function isAdded(prompt: ExtractionPromptSearchEntry): boolean {
  return (prompt.contract_types ?? []).some(type => type.uid === props.contractTypeUid);
}

function isPicked(prompt: ExtractionPromptSearchEntry): boolean {
  return picked.value.some(entry => entry.uid === prompt.uid);
}

function toggle(prompt: ExtractionPromptSearchEntry): void {
  if (isAdded(prompt)) return;
  picked.value = isPicked(prompt)
    ? picked.value.filter(entry => entry.uid !== prompt.uid)
    : [...picked.value, prompt];
}

const emptyTitle = computed(() =>
  query.value.trim() || filter.value
    ? 'No prompts match your search'
    : 'No prompts in this organisation'
);

const addLabel = computed(() => {
  const count = picked.value.length;
  if (count === 0) return 'Add prompts';
  return `Add ${count} ${count === 1 ? 'prompt' : 'prompts'}`;
});

function close(): void {
  if (adding.value) return;
  emit('update:show', false);
}

/**
 * There is no endpoint that links a prompt to a contract type, so each prompt is updated with
 * its contract types plus this one: one request per prompt.
 */
async function handleAdd(): Promise<void> {
  if (picked.value.length === 0) return;
  adding.value = true;
  const toAdd = picked.value;
  const results = await Promise.allSettled(
    toAdd.map(prompt =>
      ExtractionPromptsService.update(
        prompt.uid,
        {
          contract_type_uids: [
            ...(prompt.contract_types ?? []).map(type => type.uid),
            props.contractTypeUid,
          ],
        },
        // A contract type edit re-extracts nothing.
        ContractManagementColumnExtractionScope.NEW_ONLY
      )
    )
  );
  adding.value = false;

  const failed = toAdd.filter((_, index) => results[index]?.status === 'rejected');
  const addedCount = toAdd.length - failed.length;
  if (addedCount > 0) emit('added');

  if (failed.length === 0) {
    message.success(addedCount === 1 ? 'Prompt added' : `${addedCount} prompts added`);
    close();
    return;
  }

  // Keep the failed ones picked so the user can retry them.
  const firstError = results.find(result => result.status === 'rejected');
  const reason =
    firstError?.reason instanceof Error ? firstError.reason.message : 'Failed to add the prompt';
  message.error(
    addedCount > 0 ? `Added ${addedCount} of ${toAdd.length} prompts. ${reason}` : reason
  );
  picked.value = failed;
  void load();
}

/** A form's wider sides than a rail's 16px; the top is the page's own padding. */
const panelStyle: CSSProperties = {
  paddingTop: gaiaShellPadding.top,
  paddingLeft: '24px',
  paddingRight: '24px',
};
</script>

<template>
  <gaia-panel :style="panelStyle">
    <template #header>
      <n-flex align="flex-start" :size="12" :wrap="false" :style="{ width: '100%' }">
        <gaia-text tag="div" size="subtitle" weight="semibold" tone="ink" :style="{ minWidth: 0 }">
          Add prompts to {{ contractTypeName }}
        </gaia-text>
        <gaia-icon-button
          :dimension="28"
          quaternary
          title="Close"
          :disabled="adding"
          :style="{ marginLeft: 'auto', flex: 'none' }"
          @click="close"
        >
          <n-icon :component="IconClose" :size="14" />
        </gaia-icon-button>
      </n-flex>
    </template>

    <n-flex vertical :size="10" :wrap="false">
      <!-- The prompts page's search and contract type filter. -->
      <n-flex align="center" :size="8" :wrap="false">
        <gaia-search-input
          v-model:value="query"
          placeholder="Search prompts"
          :debounce="500"
          :style="{ flex: '1 1 auto', minWidth: 0 }"
          @search="applySearch"
        />
        <gaia-contract-type-filter v-model="pickedTypeKeys" without-option />
      </n-flex>

      <n-flex justify="end">
        <gaia-sort-control
          v-model:value="sortKey"
          v-model:direction="sortDirection"
          :options="SORTS"
        />
      </n-flex>

      <gaia-empty-state v-if="error" :title="error" />

      <gaia-spinner v-else-if="loading" :style="{ margin: '28px auto' }" />

      <gaia-empty-state v-else-if="prompts.length === 0" :title="emptyTitle" />

      <template v-else>
        <n-flex vertical :size="0" :wrap="false">
          <gaia-picker-option
            v-for="prompt in prompts"
            :key="prompt.uid"
            :label="prompt.display_name"
            :meta="prompt.prompt"
            :selected="isPicked(prompt)"
            :disabled="isAdded(prompt)"
            :title="prompt.prompt"
            checkbox
            @select="toggle(prompt)"
          >
            <template #trailing>
              <gaia-tag variant="muted" size="tiny">
                {{ responseTypeLabel(prompt.response_type) }}
              </gaia-tag>
            </template>
          </gaia-picker-option>
        </n-flex>
      </template>
    </n-flex>

    <!-- The pager is pinned with the buttons, so the list fills the space above it. -->
    <template #footer>
      <gaia-pagination
        v-if="!error && total > 0"
        :page="page"
        :page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50]"
        :style="{ padding: 0, marginBottom: '12px' }"
        @update:page="(value: number) => (page = value)"
        @update:page-size="
          (value: number) => {
            pageSize = value;
            page = 1;
          }
        "
      />
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button size="small" :disabled="adding" @click="close">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="adding"
          :disabled="picked.length === 0"
          @click="handleAdd"
        >
          {{ addLabel }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-panel>
</template>
